
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import RunnitDynamicFieldLabelRow from '@/views/Runnits/RunnitSettings/RunnitDynamicFieldLabelRow.vue';
import RunnitAsset3D from '@/views/Runnits/RunnitAsset3D.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import { RunnitDynamicFieldMixin } from '@/views/Runnits/RunnitSettings/mixins/RunnitDynamicFieldMixin';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import { ASSET_3D_ACCEPT_ATTRIBUTE } from '@/views/Runnits/RunnitSettings/Asset3D/asset3DFormats';
import { uploadAsset3DToRunnit } from '@/utils/runnitUpload';
import { RunnitNodeRunInputValue } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';

export default Vue.extend({
	name: 'Asset3DInput',
	mixins: [
		RunnitDynamicFieldMixin,
		RunnitsImageSelectMixin,
	],
	props: {
		field: { type: Object, required: true },
		value: { type: Object, default: null },
		nodeId: { type: String, default: null },
		adminOnly: { type: Boolean, default: false },
	},
	data () {
		return {
			ASSET_3D_ACCEPT_ATTRIBUTE,
			IMAGE_GALLERY_DIALOG_NAV,
			isUploading: false,
		};
	},
	destroyed () {
		this.exitSelectionMode(this.selectionStateTriggerId);
	},
	computed: {
		...mapState([
			'user',
		]),
		assetValue (): RunnitNodeRunInputValue | null {
			return this.value || null;
		},
		selectionStateTriggerId (): string {
			return `asset-3d-input-${this.field.uuid}`;
		},
		selectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.selectionStateTriggerId);
		},
	},
	watch: {
		selectionStateForTrigger: {
			immediate: false,
			handler (newVal, oldVal) {
				this.commitSingleSelectionStateValue(
					newVal,
					oldVal,
					this.field.uuid,
					this._get(this.assetValue, 'nodeRunResult.uuid') || null,
					this._get(this.assetValue, 'upload.id') || null,
					() => this.onInput(null),
					(value) => this.onInput(value),
				);
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
		]),
		onInput (asset: RunnitNodeRunInputValue | null): void {
			this.$emit('input', asset);
		},
		clearValueAndStartSelectSelectionMode (): void {
			this.onInput(null);
			this.clearSelectionsFromState(this.selectionStateTriggerId);
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.selectionStateTriggerId, this.field, ['ASSET_3D']);
		},
		startLibrarySelectionMode (imageGalleryDialogInitialNav: ImageGalleryDialogNav): void {
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav,
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.selectionStateTriggerId, this.field, ['ASSET_3D']);
		},
		openFileDialog (): void {
			const input = this.$refs.fileInput as HTMLInputElement;
			if (input) input.click();
		},
		async onFileChange (event: Event): Promise<void> {
			const input = event.target as HTMLInputElement;
			const file = input.files && input.files[0];
			if (!file) return;

			try {
				this.isUploading = true;
				const upload = await uploadAsset3DToRunnit(file, this.user.id);
				this.onInput({ upload });
			} catch (err: any) {
				console.error('ASSET_3D upload failed', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: err && err.message ? err.message : 'Failed to upload 3D asset',
					show: true,
				});
			} finally {
				this.isUploading = false;
				input.value = '';
			}
		},
	},
	components: {
		BaseButton,
		RunnitAsset3D,
		RunnitDynamicFieldLabelRow,
	},
});
